import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Plus, TrendingUp, ArrowUpRight } from "lucide-react";
import api from "../lib/api";

/**
 * DiploksBurnMeter — compact 7-day usage sparkline + soft top-up nudge.
 *
 * Self-hides for policy-restricted roles (the burn-meter endpoint
 * returns `eligible=false`).  Refreshes on the global
 * `diploks:wallet-changed` window event so a fresh consume bumps the
 * meter immediately.
 */
export default function DiploksBurnMeter({ days = 7 }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/diploks/burn-meter?days=${days}`);
      if (!r.data.eligible) { setHidden(true); return; }
      setHidden(false);
      setData(r.data);
    } catch { setHidden(true); }
  }, [days]);

  useEffect(() => {
    let alive = true;
    const tick = () => { if (alive) refresh(); };
    tick();
    const onChange = () => tick();
    window.addEventListener("diploks:wallet-changed", onChange);
    return () => {
      alive = false;
      window.removeEventListener("diploks:wallet-changed", onChange);
    };
  }, [refresh]);

  if (hidden || !data) return null;
  const buckets = data.buckets || [];
  const max = Math.max(1, ...buckets.map((b) => b.used));
  const total = data.total_used || 0;

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-4"
      data-testid="diploks-burn-meter"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold flex items-center gap-1">
            <Flame className="h-3 w-3 text-orange-500" /> Diploks burn — last {data.days} days
          </p>
          <p
            className="mt-1 text-2xl font-bold text-[#0A192F] tabular-nums"
            data-testid="diploks-burn-meter-total"
          >
            {total}
            <span className="text-sm text-slate-500 font-medium ml-1">used</span>
          </p>
          <p className="text-[10px] text-slate-500">
            Average · {data.average_per_day || 0}/day
          </p>
        </div>
        {total > 0 && (
          <Link
            to="/dashboard/diploks/personal/topup"
            data-testid="diploks-burn-meter-topup-cta"
            className="text-[11px] inline-flex items-center gap-1 text-[#0F4C81] hover:underline font-semibold"
          >
            <Plus className="h-3 w-3" /> Top up
          </Link>
        )}
      </div>

      {/* Bar sparkline */}
      <div
        className="mt-3 flex items-end gap-1 h-12"
        data-testid="diploks-burn-meter-sparkline"
      >
        {buckets.map((b) => {
          const h = b.used > 0 ? Math.max(4, (b.used / max) * 100) : 4;
          const isToday = b.date === buckets[buckets.length - 1]?.date;
          return (
            <div
              key={b.date}
              data-testid={`diploks-burn-meter-bar-${b.date}`}
              title={`${b.used} Diploks · ${b.events} events · ${b.date}`}
              className="flex-1 rounded-t-sm transition-all"
              style={{
                height: `${h}%`,
                backgroundColor: b.used > 0
                  ? (isToday ? "#D4AF37" : "#0F4C81")
                  : "#E2E8F0",
              }}
            />
          );
        })}
      </div>
      <div className="mt-1.5 flex justify-between text-[9px] text-slate-400 font-medium">
        {buckets.map((b, i) => (
          <span key={b.date} className="tabular-nums">
            {b.date.slice(5)}
          </span>
        ))}
      </div>

      {total === 0 ? (
        <p className="mt-3 text-[11px] text-slate-500">
          No Diploks used yet — try a premium feature like AI Paper Review.
        </p>
      ) : data.average_per_day >= 3 ? (
        <p
          className="mt-3 text-[11px] text-[#0A192F] flex items-center gap-1"
          data-testid="diploks-burn-meter-nudge"
        >
          <TrendingUp className="h-3 w-3 text-emerald-600" />
          You&apos;re on a roll! Keep your AI prep streak going — top up to
          stay ahead.
        </p>
      ) : (
        <p className="mt-3 text-[11px] text-slate-500">
          Light usage so far. Premium features are ready when you need them.
        </p>
      )}
    </div>
  );
}
